import { useState } from 'react';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import { useLang } from '../i18n';

const css = `
  .help { padding: 6px 16px 32px; }
  .help-hero { margin-bottom: 20px; padding: 16px 14px; border-radius: 12px; background: linear-gradient(135deg, rgba(129,135,246,.15), rgba(118,225,255,.06)); box-shadow: inset 0 0 0 1px rgba(118,225,255,.18); }
  .help-hero-title { font-size: 17px; font-weight: 800; color: #fff; }
  .help-hero-sub { font-size: 12px; color: #76e1ff; margin-top: 4px; }
  .help-section-title { font-size: 13px; font-weight: 800; color: #fff; margin: 18px 0 10px; display: flex; align-items: center; gap: 7px; }
  .help-section-title::before { content: ''; display: block; width: 3px; height: 14px; border-radius: 2px; background: linear-gradient(#8187f6, #76e1ff); flex: none; }
  .help-faq-item { border-radius: 10px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.07); margin-bottom: 6px; overflow: hidden; }
  .help-faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 14px; font-size: 13px; font-weight: 600; color: #cfcee0; text-align: left; background: none; border: none; cursor: pointer; font-family: inherit; }
  .help-faq-q svg { flex: none; transition: transform .2s; }
  .help-faq-item.open .help-faq-q svg { transform: rotate(180deg); }
  .help-faq-a { display: none; padding: 0 14px 12px; font-size: 12px; color: #9b9ab0; line-height: 1.6; }
  .help-faq-item.open .help-faq-a { display: block; }
  .help-contact { margin-top: 22px; padding: 14px; border-radius: 12px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.07); text-align: center; }
  .help-contact-title { font-size: 13px; font-weight: 800; color: #fff; margin-bottom: 6px; }
  .help-contact-sub { font-size: 12px; color: #9b9ab0; line-height: 1.5; }
  .help-contact-email { display: inline-block; margin-top: 10px; font-size: 13px; font-weight: 700; color: #76e1ff; }
`;

const CONTENT = {
  fr: {
    backTitle: 'Centre d\'aide',
    heroTitle: 'Centre d\'aide',
    heroSub: 'Trouvez rapidement une réponse à votre question',
    contactTitle: 'Besoin d\'aide supplémentaire ?',
    contactSub: 'Notre équipe support est disponible 7j/7 pour vous accompagner.',
    sections: [
      {
        title: 'Compte & Inscription',
        faqs: [
          { q: 'Comment créer un compte ?', a: 'Cliquez sur "S\'inscrire", renseignez votre email, choisissez un mot de passe et validez. Un email de confirmation vous sera envoyé.' },
          { q: 'J\'ai oublié mon mot de passe, que faire ?', a: 'Sur la page de connexion, cliquez sur "Mot de passe oublié" et suivez les instructions reçues par email pour le réinitialiser.' },
          { q: 'Puis-je avoir plusieurs comptes ?', a: 'Non, chaque utilisateur ne peut posséder qu\'un seul compte. La création de comptes multiples entraîne la suspension immédiate.' },
        ],
      },
      {
        title: 'Dépôts & Retraits',
        faqs: [
          { q: 'Quels sont les moyens de paiement acceptés ?', a: 'Nous acceptons les cartes bancaires (Visa, Mastercard), Apple Pay, Google Pay, ainsi que les cryptomonnaies (BTC, ETH, USDT) selon votre région.' },
          { q: 'Combien de temps prend un retrait ?', a: 'Les retraits sont traités sous 24 à 72h ouvrées selon le moyen de paiement choisi. Les cryptos sont généralement les plus rapides.' },
          { q: 'Mon paiement a échoué, que faire ?', a: 'Vérifiez que votre carte n\'est pas bloquée pour les transactions en ligne. Certaines banques bloquent les paiements liés aux jeux. Essayez un autre moyen de paiement ou contactez votre banque.' },
        ],
      },
      {
        title: 'Paris sportifs',
        faqs: [
          { q: 'Comment placer un pari ?', a: 'Sélectionnez un événement, cliquez sur la cote souhaitée — elle s\'ajoute au ticket de pari. Entrez votre mise et confirmez.' },
          { q: 'Puis-je annuler un pari ?', a: 'Non, une fois confirmé, un pari ne peut pas être annulé. Vérifiez toujours votre sélection avant de valider.' },
          { q: 'Quand mes gains sont-ils crédités ?', a: 'Les gains sont crédités automatiquement sur votre solde dès que le résultat de l\'événement est officiellement confirmé.' },
        ],
      },
    ],
  },
  es: {
    backTitle: 'Centro de ayuda',
    heroTitle: 'Centro de ayuda',
    heroSub: 'Encuentre rápidamente una respuesta a su pregunta',
    contactTitle: '¿Necesita ayuda adicional?',
    contactSub: 'Nuestro equipo de soporte está disponible los 7 días de la semana para ayudarle.',
    sections: [
      {
        title: 'Cuenta y Registro',
        faqs: [
          { q: '¿Cómo crear una cuenta?', a: 'Haga clic en "Registrarse", indique su correo electrónico, elija una contraseña y confirme. Recibirá un correo de confirmación.' },
          { q: 'He olvidado mi contraseña, ¿qué hago?', a: 'En la página de inicio de sesión, haga clic en "Contraseña olvidada" y siga las instrucciones recibidas por correo para restablecerla.' },
          { q: '¿Puedo tener varias cuentas?', a: 'No, cada usuario solo puede tener una cuenta. La creación de múltiples cuentas conlleva la suspensión inmediata.' },
        ],
      },
      {
        title: 'Depósitos y Retiros',
        faqs: [
          { q: '¿Cuáles son los métodos de pago aceptados?', a: 'Aceptamos tarjetas bancarias (Visa, Mastercard), Apple Pay, Google Pay, así como criptomonedas (BTC, ETH, USDT) según su región.' },
          { q: '¿Cuánto tarda un retiro?', a: 'Los retiros se procesan en un plazo de 24 a 72 h hábiles según el método de pago elegido. Las criptomonedas suelen ser las más rápidas.' },
          { q: 'Mi pago ha fallado, ¿qué hago?', a: 'Compruebe que su tarjeta no esté bloqueada para transacciones en línea. Algunos bancos bloquean los pagos relacionados con el juego. Pruebe con otro método de pago o contacte con su banco.' },
        ],
      },
      {
        title: 'Apuestas deportivas',
        faqs: [
          { q: '¿Cómo realizar una apuesta?', a: 'Seleccione un evento, haga clic en la cuota deseada — se añade al boleto de apuesta. Introduzca su importe y confirme.' },
          { q: '¿Puedo cancelar una apuesta?', a: 'No, una vez confirmada, una apuesta no puede cancelarse. Compruebe siempre su selección antes de validar.' },
          { q: '¿Cuándo se acreditan mis ganancias?', a: 'Las ganancias se acreditan automáticamente en su saldo en cuanto el resultado del evento se confirma oficialmente.' },
        ],
      },
    ],
  },
  pt: {
    backTitle: 'Central de ajuda',
    heroTitle: 'Central de ajuda',
    heroSub: 'Encontre rapidamente uma resposta para sua dúvida',
    contactTitle: 'Precisa de mais ajuda?',
    contactSub: 'Nossa equipe de suporte está disponível 7 dias por semana para ajudar você.',
    sections: [
      {
        title: 'Conta e Cadastro',
        faqs: [
          { q: 'Como criar uma conta?', a: 'Clique em "Cadastre-se", informe seu e-mail, escolha uma senha e confirme. Um e-mail de confirmação será enviado.' },
          { q: 'Esqueci minha senha, o que fazer?', a: 'Na página de login, clique em "Esqueceu a senha" e siga as instruções recebidas por e-mail para redefini-la.' },
          { q: 'Posso ter várias contas?', a: 'Não, cada usuário pode ter apenas uma conta. A criação de múltiplas contas resulta na suspensão imediata.' },
        ],
      },
      {
        title: 'Depósitos e Saques',
        faqs: [
          { q: 'Quais são os métodos de pagamento aceitos?', a: 'Aceitamos cartões (Visa, Mastercard), Apple Pay, Google Pay, além de criptomoedas (BTC, ETH, USDT) conforme sua região.' },
          { q: 'Quanto tempo leva um saque?', a: 'Os saques são processados em 24 a 72h úteis, conforme o método de pagamento escolhido. As criptos geralmente são as mais rápidas.' },
          { q: 'Meu pagamento falhou, o que fazer?', a: 'Verifique se seu cartão não está bloqueado para transações online. Alguns bancos bloqueiam pagamentos ligados a jogos. Tente outro método de pagamento ou entre em contato com seu banco.' },
        ],
      },
      {
        title: 'Apostas esportivas',
        faqs: [
          { q: 'Como fazer uma aposta?', a: 'Selecione um evento, clique na odd desejada — ela é adicionada ao cupom de apostas. Insira seu valor e confirme.' },
          { q: 'Posso cancelar uma aposta?', a: 'Não, uma vez confirmada, uma aposta não pode ser cancelada. Sempre confira sua seleção antes de validar.' },
          { q: 'Quando meus ganhos são creditados?', a: 'Os ganhos são creditados automaticamente no seu saldo assim que o resultado do evento é oficialmente confirmado.' },
        ],
      },
    ],
  },
  en: {
    backTitle: 'Help Center',
    heroTitle: 'Help Center',
    heroSub: 'Quickly find an answer to your question',
    contactTitle: 'Need more help?',
    contactSub: 'Our support team is available 7 days a week to assist you.',
    sections: [
      {
        title: 'Account & Registration',
        faqs: [
          { q: 'How do I create an account?', a: 'Click "Sign up", enter your email, choose a password and confirm. A confirmation email will be sent to you.' },
          { q: 'I forgot my password, what should I do?', a: 'On the login page, click "Forgot password" and follow the instructions received by email to reset it.' },
          { q: 'Can I have multiple accounts?', a: 'No, each user can only have one account. Creating multiple accounts leads to immediate suspension.' },
        ],
      },
      {
        title: 'Deposits & Withdrawals',
        faqs: [
          { q: 'What payment methods are accepted?', a: 'We accept bank cards (Visa, Mastercard), Apple Pay, Google Pay, as well as cryptocurrencies (BTC, ETH, USDT) depending on your region.' },
          { q: 'How long does a withdrawal take?', a: 'Withdrawals are processed within 24 to 72 business hours depending on the chosen payment method. Cryptos are usually the fastest.' },
          { q: 'My payment failed, what should I do?', a: 'Check that your card is not blocked for online transactions. Some banks block payments related to gaming. Try another payment method or contact your bank.' },
        ],
      },
      {
        title: 'Sports betting',
        faqs: [
          { q: 'How do I place a bet?', a: 'Select an event, click the odds you want — it is added to the betslip. Enter your stake and confirm.' },
          { q: 'Can I cancel a bet?', a: 'No, once confirmed, a bet cannot be cancelled. Always check your selection before validating.' },
          { q: 'When are my winnings credited?', a: 'Winnings are credited automatically to your balance as soon as the event result is officially confirmed.' },
        ],
      },
    ],
  },
  it: {
    backTitle: 'Centro assistenza',
    heroTitle: 'Centro assistenza',
    heroSub: 'Trova rapidamente una risposta alla tua domanda',
    contactTitle: 'Hai bisogno di ulteriore aiuto?',
    contactSub: 'Il nostro team di assistenza è disponibile 7 giorni su 7 per aiutarti.',
    sections: [
      {
        title: 'Account e Registrazione',
        faqs: [
          { q: 'Come creo un account?', a: 'Clicca su "Registrati", inserisci la tua email, scegli una password e conferma. Riceverai un\'email di conferma.' },
          { q: 'Ho dimenticato la password, cosa faccio?', a: 'Nella pagina di accesso, clicca su "Password dimenticata" e segui le istruzioni ricevute via email per reimpostarla.' },
          { q: 'Posso avere più account?', a: 'No, ogni utente può avere un solo account. La creazione di più account comporta la sospensione immediata.' },
        ],
      },
      {
        title: 'Depositi e Prelievi',
        faqs: [
          { q: 'Quali metodi di pagamento sono accettati?', a: 'Accettiamo carte (Visa, Mastercard), Apple Pay, Google Pay, oltre alle criptovalute (BTC, ETH, USDT) in base alla tua regione.' },
          { q: 'Quanto tempo richiede un prelievo?', a: 'I prelievi vengono elaborati entro 24-72 ore lavorative in base al metodo di pagamento scelto. Le cripto sono di solito le più veloci.' },
          { q: 'Il mio pagamento è fallito, cosa faccio?', a: 'Verifica che la tua carta non sia bloccata per le transazioni online. Alcune banche bloccano i pagamenti legati al gioco. Prova un altro metodo di pagamento o contatta la tua banca.' },
        ],
      },
      {
        title: 'Scommesse sportive',
        faqs: [
          { q: 'Come piazzo una scommessa?', a: 'Seleziona un evento, clicca sulla quota desiderata — viene aggiunta alla schedina. Inserisci la tua puntata e conferma.' },
          { q: 'Posso annullare una scommessa?', a: 'No, una volta confermata, una scommessa non può essere annullata. Controlla sempre la tua selezione prima di convalidare.' },
          { q: 'Quando vengono accreditate le mie vincite?', a: 'Le vincite vengono accreditate automaticamente sul tuo saldo non appena il risultato dell\'evento è ufficialmente confermato.' },
        ],
      },
    ],
  },
  nl: {
    backTitle: 'Helpcentrum',
    heroTitle: 'Helpcentrum',
    heroSub: 'Vind snel een antwoord op je vraag',
    contactTitle: 'Meer hulp nodig?',
    contactSub: 'Ons supportteam is 7 dagen per week beschikbaar om je te helpen.',
    sections: [
      {
        title: 'Account & Registratie',
        faqs: [
          { q: 'Hoe maak ik een account aan?', a: 'Klik op "Registreren", voer je e-mail in, kies een wachtwoord en bevestig. Je ontvangt een bevestigingsmail.' },
          { q: 'Ik ben mijn wachtwoord vergeten, wat moet ik doen?', a: 'Klik op de inlogpagina op "Wachtwoord vergeten" en volg de per e-mail ontvangen instructies om het opnieuw in te stellen.' },
          { q: 'Kan ik meerdere accounts hebben?', a: 'Nee, elke gebruiker kan slechts één account hebben. Het aanmaken van meerdere accounts leidt tot onmiddellijke schorsing.' },
        ],
      },
      {
        title: 'Stortingen & Opnames',
        faqs: [
          { q: 'Welke betaalmethoden worden geaccepteerd?', a: 'Wij accepteren bankkaarten (Visa, Mastercard), Apple Pay, Google Pay, evenals cryptovaluta (BTC, ETH, USDT) afhankelijk van je regio.' },
          { q: 'Hoe lang duurt een opname?', a: 'Opnames worden binnen 24 tot 72 werkuren verwerkt, afhankelijk van de gekozen betaalmethode. Crypto is meestal het snelst.' },
          { q: 'Mijn betaling is mislukt, wat moet ik doen?', a: 'Controleer of je kaart niet is geblokkeerd voor online transacties. Sommige banken blokkeren aan gokken gerelateerde betalingen. Probeer een andere betaalmethode of neem contact op met je bank.' },
        ],
      },
      {
        title: 'Sportweddenschappen',
        faqs: [
          { q: 'Hoe plaats ik een weddenschap?', a: 'Selecteer een evenement, klik op de gewenste odds — deze wordt toegevoegd aan het weddenschapbon. Voer je inzet in en bevestig.' },
          { q: 'Kan ik een weddenschap annuleren?', a: 'Nee, eenmaal bevestigd kan een weddenschap niet worden geannuleerd. Controleer altijd je selectie voordat je bevestigt.' },
          { q: 'Wanneer wordt mijn winst bijgeschreven?', a: 'Winst wordt automatisch op je saldo bijgeschreven zodra het resultaat van het evenement officieel is bevestigd.' },
        ],
      },
    ],
  },
  tr: {
    backTitle: 'Yardım Merkezi',
    heroTitle: 'Yardım Merkezi',
    heroSub: 'Sorunuza hızlıca yanıt bulun',
    contactTitle: 'Daha fazla yardıma mı ihtiyacınız var?',
    contactSub: 'Destek ekibimiz size yardımcı olmak için haftanın 7 günü hizmetinizdedir.',
    sections: [
      {
        title: 'Hesap ve Kayıt',
        faqs: [
          { q: 'Nasıl hesap oluştururum?', a: '"Kaydol"a tıklayın, e-postanızı girin, bir şifre seçin ve onaylayın. Size bir onay e-postası gönderilecektir.' },
          { q: 'Şifremi unuttum, ne yapmalıyım?', a: 'Giriş sayfasında "Şifremi unuttum"a tıklayın ve sıfırlamak için e-posta ile aldığınız talimatları izleyin.' },
          { q: 'Birden fazla hesabım olabilir mi?', a: 'Hayır, her kullanıcının yalnızca bir hesabı olabilir. Birden fazla hesap oluşturmak anında askıya alınmaya yol açar.' },
        ],
      },
      {
        title: 'Yatırımlar ve Çekimler',
        faqs: [
          { q: 'Hangi ödeme yöntemleri kabul edilir?', a: 'Banka kartlarını (Visa, Mastercard), Apple Pay, Google Pay ve bölgenize göre kripto paraları (BTC, ETH, USDT) kabul ediyoruz.' },
          { q: 'Bir çekim ne kadar sürer?', a: 'Çekimler, seçilen ödeme yöntemine göre 24 ila 72 iş saati içinde işlenir. Kriptolar genellikle en hızlısıdır.' },
          { q: 'Ödemem başarısız oldu, ne yapmalıyım?', a: 'Kartınızın çevrimiçi işlemler için bloke olmadığından emin olun. Bazı bankalar oyunla ilgili ödemeleri engeller. Başka bir ödeme yöntemi deneyin veya bankanızla iletişime geçin.' },
        ],
      },
      {
        title: 'Spor bahisleri',
        faqs: [
          { q: 'Nasıl bahis yaparım?', a: 'Bir etkinlik seçin, istediğiniz orana tıklayın — bahis kuponuna eklenir. Bahis tutarınızı girin ve onaylayın.' },
          { q: 'Bir bahsi iptal edebilir miyim?', a: 'Hayır, onaylandıktan sonra bir bahis iptal edilemez. Onaylamadan önce daima seçiminizi kontrol edin.' },
          { q: 'Kazançlarım ne zaman hesabıma geçer?', a: 'Kazançlar, etkinlik sonucu resmi olarak onaylanır onaylanmaz otomatik olarak bakiyenize eklenir.' },
        ],
      },
    ],
  },
  el: {
    backTitle: 'Κέντρο βοήθειας',
    heroTitle: 'Κέντρο βοήθειας',
    heroSub: 'Βρείτε γρήγορα μια απάντηση στην ερώτησή σας',
    contactTitle: 'Χρειάζεστε περισσότερη βοήθεια;',
    contactSub: 'Η ομάδα υποστήριξής μας είναι διαθέσιμη 7 ημέρες την εβδομάδα για να σας βοηθήσει.',
    sections: [
      {
        title: 'Λογαριασμός & Εγγραφή',
        faqs: [
          { q: 'Πώς δημιουργώ λογαριασμό;', a: 'Κάντε κλικ στο "Εγγραφή", εισαγάγετε το email σας, επιλέξτε κωδικό και επιβεβαιώστε. Θα σας σταλεί email επιβεβαίωσης.' },
          { q: 'Ξέχασα τον κωδικό μου, τι να κάνω;', a: 'Στη σελίδα σύνδεσης, κάντε κλικ στο "Ξεχάσατε τον κωδικό" και ακολουθήστε τις οδηγίες που λάβατε μέσω email για να τον επαναφέρετε.' },
          { q: 'Μπορώ να έχω πολλούς λογαριασμούς;', a: 'Όχι, κάθε χρήστης μπορεί να έχει μόνο έναν λογαριασμό. Η δημιουργία πολλών λογαριασμών οδηγεί σε άμεση αναστολή.' },
        ],
      },
      {
        title: 'Καταθέσεις & Αναλήψεις',
        faqs: [
          { q: 'Ποιες μέθοδοι πληρωμής γίνονται δεκτές;', a: 'Δεχόμαστε τραπεζικές κάρτες (Visa, Mastercard), Apple Pay, Google Pay, καθώς και κρυπτονομίσματα (BTC, ETH, USDT) ανάλογα με την περιοχή σας.' },
          { q: 'Πόσο διαρκεί μια ανάληψη;', a: 'Οι αναλήψεις επεξεργάζονται εντός 24 έως 72 εργάσιμων ωρών ανάλογα με τη μέθοδο πληρωμής. Τα κρυπτονομίσματα είναι συνήθως τα ταχύτερα.' },
          { q: 'Η πληρωμή μου απέτυχε, τι να κάνω;', a: 'Ελέγξτε ότι η κάρτα σας δεν είναι μπλοκαρισμένη για διαδικτυακές συναλλαγές. Ορισμένες τράπεζες μπλοκάρουν πληρωμές σχετικές με τυχερά παιχνίδια. Δοκιμάστε άλλη μέθοδο πληρωμής ή επικοινωνήστε με την τράπεζά σας.' },
        ],
      },
      {
        title: 'Αθλητικός στοιχηματισμός',
        faqs: [
          { q: 'Πώς τοποθετώ ένα στοίχημα;', a: 'Επιλέξτε ένα γεγονός, κάντε κλικ στην απόδοση που θέλετε — προστίθεται στο δελτίο. Εισαγάγετε το ποντάρισμά σας και επιβεβαιώστε.' },
          { q: 'Μπορώ να ακυρώσω ένα στοίχημα;', a: 'Όχι, μόλις επιβεβαιωθεί, ένα στοίχημα δεν μπορεί να ακυρωθεί. Ελέγχετε πάντα την επιλογή σας πριν την επικύρωση.' },
          { q: 'Πότε πιστώνονται τα κέρδη μου;', a: 'Τα κέρδη πιστώνονται αυτόματα στο υπόλοιπό σας μόλις το αποτέλεσμα του γεγονότος επιβεβαιωθεί επίσημα.' },
        ],
      },
    ],
  },
  ro: {
    backTitle: 'Centru de ajutor',
    heroTitle: 'Centru de ajutor',
    heroSub: 'Găsiți rapid un răspuns la întrebarea dvs.',
    contactTitle: 'Aveți nevoie de mai mult ajutor?',
    contactSub: 'Echipa noastră de asistență este disponibilă 7 zile din 7 pentru a vă ajuta.',
    sections: [
      {
        title: 'Cont și Înregistrare',
        faqs: [
          { q: 'Cum creez un cont?', a: 'Faceți clic pe "Înregistrare", introduceți e-mailul, alegeți o parolă și confirmați. Veți primi un e-mail de confirmare.' },
          { q: 'Mi-am uitat parola, ce fac?', a: 'Pe pagina de autentificare, faceți clic pe "Parolă uitată" și urmați instrucțiunile primite prin e-mail pentru a o reseta.' },
          { q: 'Pot avea mai multe conturi?', a: 'Nu, fiecare utilizator poate avea un singur cont. Crearea mai multor conturi duce la suspendarea imediată.' },
        ],
      },
      {
        title: 'Depuneri și Retrageri',
        faqs: [
          { q: 'Ce metode de plată sunt acceptate?', a: 'Acceptăm carduri (Visa, Mastercard), Apple Pay, Google Pay, precum și criptomonede (BTC, ETH, USDT) în funcție de regiunea dvs.' },
          { q: 'Cât durează o retragere?', a: 'Retragerile sunt procesate în 24 până la 72 de ore lucrătoare, în funcție de metoda de plată aleasă. Criptomonedele sunt de obicei cele mai rapide.' },
          { q: 'Plata mea a eșuat, ce fac?', a: 'Verificați dacă cardul dvs. nu este blocat pentru tranzacții online. Unele bănci blochează plățile legate de jocuri. Încercați altă metodă de plată sau contactați banca.' },
        ],
      },
      {
        title: 'Pariuri sportive',
        faqs: [
          { q: 'Cum plasez un pariu?', a: 'Selectați un eveniment, faceți clic pe cota dorită — se adaugă în biletul de pariu. Introduceți miza și confirmați.' },
          { q: 'Pot anula un pariu?', a: 'Nu, odată confirmat, un pariu nu poate fi anulat. Verificați întotdeauna selecția înainte de a valida.' },
          { q: 'Când sunt creditate câștigurile mele?', a: 'Câștigurile sunt creditate automat în soldul dvs. imediat ce rezultatul evenimentului este confirmat oficial.' },
        ],
      },
    ],
  },
};

export default function HelpCenter() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  const [open, setOpen] = useState(null);

  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={c.backTitle} />
      <div className="help">
        <div className="help-hero">
          <div className="help-hero-title">{c.heroTitle}</div>
          <div className="help-hero-sub">{c.heroSub}</div>
        </div>

        {c.sections.map((section) => (
          <div key={section.title}>
            <div className="help-section-title">{section.title}</div>
            {section.faqs.map((item, i) => {
              const key = section.title + i;
              return (
                <div key={key} className={'help-faq-item' + (open === key ? ' open' : '')}>
                  <button className="help-faq-q" onClick={() => setOpen(open === key ? null : key)}>
                    <span>{item.q}</span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
                  </button>
                  <div className="help-faq-a">{item.a}</div>
                </div>
              );
            })}
          </div>
        ))}

        <div className="help-contact">
          <div className="help-contact-title">{c.contactTitle}</div>
          <div className="help-contact-sub">{c.contactSub}</div>
          <span className="help-contact-email">support@roxs-sports.com</span>
        </div>
      </div>
    </PhoneShell>
  );
}
